import { HTMLAttributes, Key } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Util } from "../util/Util";
import { CatalogItemOptionActions, useCatalogItemPicker } from "./CatalogItemActions";

export type ItemPriceMode = "selling" | "cost" | "both";

export interface CatalogItemOption {
	id?: number | string | null;
	ItemName?: string;
	SellingPrice?: number;
	CostPrice?: number;
	/** false only when the endpoint was asked for catalog items beyond the depot. */
	InDepot?: boolean;
	IsMetered?: boolean;
	[key: string]: unknown;
}

/**
 * Marks an option the depot does not stock yet. Labelled inline rather than
 * grouped at the bottom of the list: in a long catalog a group footer is
 * scrolled past and never seen. Unmetered items are never tagged — they hold
 * no stock anywhere, so "not in depot" would be true of them everywhere.
 */
function NotInDepotTag() {
	const { t } = useTranslation("common");

	return (
		<Tooltip title={t("catalog.notInDepotHint")} placement="top" disableFocusListener>
			<Chip
				component="span"
				size="small"
				label={t("catalog.notInDepot")}
				color="warning"
				variant="outlined"
				sx={{
					flexShrink: 0,
					height: 16,
					fontSize: "0.6rem",
					fontWeight: 600,
					"& .MuiChip-label": { px: 0.6 },
				}}
			/>
		</Tooltip>
	);
}

function DualPricePreview({ option }: { option: CatalogItemOption }) {
	const { t } = useTranslation("common");
	const cost = Number(option.CostPrice ?? 0);
	const sell = Number(option.SellingPrice ?? 0);

	return (
		<Box
			component="span"
			sx={{
				display: "flex",
				alignItems: "baseline",
				gap: 0.75,
				fontVariantNumeric: "tabular-nums",
				lineHeight: 1.2,
			}}
		>
			<Typography
				component="span"
				sx={{
					color: "warning.main",
					fontWeight: 400,
					fontSize: "0.68rem",
					whiteSpace: "nowrap",
				}}
			>
				{t("catalog.priceCostAbbr")} {Util.formatNumber(cost)}
			</Typography>
			<Typography
				component="span"
				sx={{
					color: "success.main",
					fontWeight: 400,
					fontSize: "0.68rem",
					whiteSpace: "nowrap",
				}}
			>
				{t("catalog.priceSellAbbr")} {Util.formatNumber(sell)}
			</Typography>
		</Box>
	);
}

function CatalogItemOptionRow({
	props,
	option,
	priceMode,
}: {
	props: HTMLAttributes<HTMLLIElement> & { key?: Key };
	option: CatalogItemOption;
	priceMode: ItemPriceMode;
}) {
	const { key, ...optionProps } = props;
	const picker = useCatalogItemPicker();
	const isPos = priceMode === "selling";
	const showActions = Boolean(picker);
	const rawPrice = priceMode === "selling" ? option.SellingPrice : option.CostPrice;
	const price = Number(rawPrice ?? 0);

	return (
		<Box
			component="li"
			key={key}
			{...optionProps}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 0.75,
				width: "100%",
				...(showActions
					? {
						"& .catalog-item-option-actions": {
							opacity: 0,
							pointerEvents: "none",
						},
						"&:hover .catalog-item-option-actions, & .catalog-item-option-actions--open": {
							opacity: 1,
							pointerEvents: "auto",
						},
					}
					: {}),
			}}
		>
			<Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
					<Box
						component="span"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{option.ItemName || ""}
					</Box>
					{option.InDepot === false && option.IsMetered !== false && <NotInDepotTag />}
				</Box>
				{priceMode === "both" && <DualPricePreview option={option} />}
			</Box>
			{priceMode !== "both" && (
				<Typography
					component="span"
					variant="caption"
					color="text.secondary"
					sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
				>
					{Util.formatNumber(price)}
				</Typography>
			)}
			{showActions && (
				<CatalogItemOptionActions itemId={option.id} itemName={option.ItemName} isPos={isPos} />
			)}
		</Box>
	);
}

/**
 * Renders an Autocomplete option with item name + price preview.
 * POS uses selling price only. Inventory / movement / supply / BOM / conversion
 * show labeled cost + selling under the name. Info/edit appear on hover when
 * the picker is wrapped in CatalogItemPickerProvider.
 */
export function renderCatalogItemOption(
	props: HTMLAttributes<HTMLLIElement> & { key?: Key },
	option: CatalogItemOption,
	priceMode: ItemPriceMode = "both",
) {
	return <CatalogItemOptionRow props={props} option={option} priceMode={priceMode} />;
}

